import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

export const XeroCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // processing, success, error
  const [message, setMessage] = useState("Connecting to Xero...");

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get("code");
      const state = searchParams.get("state");
      const error = searchParams.get("error");

      if (error) {
        setStatus("error");
        setMessage(`Xero authorization failed: ${error}`);
        return;
      }

      if (!code || !state) {
        setStatus("error");
        setMessage("Invalid callback parameters");
        return;
      }

      try {
        const response = await axios.get(
          `${API}/xero/callback?code=${code}&state=${state}`
        );

        if (response.data.success) {
          setStatus("success");
          setMessage(response.data.message || "Successfully connected to Xero!");
          toast.success("Xero connected successfully!");
          
          // Redirect after 2 seconds
          setTimeout(() => {
            navigate("/accounts/xero");
          }, 2000);
        } else {
          setStatus("error");
          setMessage(response.data.message || "Failed to connect to Xero");
        }
      } catch (error) {
        setStatus("error");
        setMessage(error.response?.data?.detail || "Failed to complete Xero authorization");
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="w-full max-w-md">
        <CardContent className="py-12 text-center">
          {status === "processing" && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-[#13B5EA] mx-auto mb-4" />
              <h2 className="text-lg font-semibold mb-2">Connecting to Xero</h2>
              <p className="text-muted-foreground">{message}</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-lg font-semibold mb-2 text-green-700">Connected!</h2>
              <p className="text-muted-foreground mb-4">{message}</p>
              <p className="text-sm text-muted-foreground">Redirecting to Xero Sync...</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold mb-2 text-red-700">Connection Failed</h2>
              <p className="text-muted-foreground mb-4">{message}</p>
              <Button onClick={() => navigate("/accounts/xero")} variant="outline">
                Back to Xero Sync
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default XeroCallbackPage;
